import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import "../styles/domain-pages.css";

const sectorCapabilities = [
  {
    id: "construction",
    tabLabel: "General Construction",
    headline: "Unifying commercial project controls and delivery margins.",
    description:
      "Move away from siloed spreadsheets held by individual project teams. We link live site variations, subcontractor progress claims, procurement purchase orders and ERP finance records into controlled reporting models.",
    signals: ["Variations", "Subcontractor claims", "Procurement POs", "ERP finance"],
  },
  {
    id: "materials",
    tabLabel: "Construction Materials",
    headline: "Eliminating data leakage between weighbridges and ledger accounts.",
    description:
      "Reconcile quarry production logs, weighbridge tickets, batch plant records and dispatch schedules automatically so product yields, logistics movement and billing lines can be explained.",
    signals: ["Weighbridge tickets", "Batch plant records", "Dispatch schedules", "Billing lines"],
  },
  {
    id: "logistics",
    tabLabel: "Heavy Logistics",
    headline: "Synchronising telematics, route efficiency and fuel records.",
    description:
      "Capture heavy vehicle telematics, route scheduling, fuel consumption and rebate datasets to isolate true margin per route, asset, depot or freight contract.",
    signals: ["Telematics", "Route logs", "Fuel records", "Fleet utilisation"],
  },
  {
    id: "mining",
    tabLabel: "Mining & Heavy Industry",
    headline: "Aggregating yield and asset metrics across remote operations.",
    description:
      "Consolidate production volumes, machinery cycle times, environmental indicators and joint-venture reporting structures into one operational data foundation.",
    signals: ["Production yield", "Cycle times", "Asset downtime", "JV reporting"],
  },
];

const industrialRisks = [
  "Scope creep, untracked subcontractor claims and fragmented project controls erode active build margins.",
  "Disconnected weighbridge tickets, haulage records and batch-plant inventory create billing and yield leakage.",
  "Telematics gaps, dead-head haulage hours and fuel rebate errors hide true logistics margin.",
  "Remote asset downtime, joint-venture reporting and haul-route performance metrics are hard to reconcile.",
];

const constructionOutcomes = [
  "A single view of project cost, progress, procurement, logistics and operational data",
  "Reduced spreadsheet handling across project controls, commercial reporting and site registers",
  "Clearer reconciliation between ERP finance, site systems, weighbridge records and operational logs",
  "Better visibility of production yield, asset utilisation, fuel usage and sustainability inputs",
  "Governed reporting layers for executives, project controls, operations and commercial teams",
  "Reusable data foundations for industrial analytics, automation and Power BI reporting",
];

export default function ConstructionData() {
  const [activeSector, setActiveSector] = useState(sectorCapabilities[0].id);
  const currentSector =
    sectorCapabilities.find((sector) => sector.id === activeSector) || sectorCapabilities[0];

  return (
    <div className="site-page construction-domain-page">
      <Seo
        title="Industrial Operations Data Platforms | Data Apparatus"
        description="Data platform engineering for construction, construction materials, quarry operations, heavy logistics, mining activity, project controls, weighbridge data and operational reporting."
      />

      <section className="page-hero construction">
        <div className="page-hero-copy">
          <span className="eyebrow">Industrial operations data</span>
          <h1>Industrial data platforms built for complex margins.</h1>
          <p>
            Connect data scattered across active construction sites, quarry weighbridges,
            batch plants, freight fleets, remote assets and project offices into governed
            reporting foundations.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Discuss industrial operations data
            </Link>
            <Link to="/domain-expertise" className="secondary-btn">
              View domain expertise
            </Link>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="content-split">
          <div>
            <span className="eyebrow">The problem</span>
            <h2>Industrial reporting breaks down when physical operations and finance data do not reconcile.</h2>
            <p>
              In construction, materials, logistics and mining, data is physically scattered
              across sites, depots, plants, vehicles, contractors and ERP modules. Without
              governed data foundations, margin leakage and operational risk stay hidden.
            </p>
          </div>
          <div className="capability-list">
            {industrialRisks.map((risk) => (
              <div key={risk}>{risk}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="industrial-sector-section">
        <div className="industrial-sector-inner">
          <div className="section-heading left">
            <span className="eyebrow">Cross-sector architecture</span>
            <h2>Operational visibility across every industrial vertical.</h2>
            <p>
              Select a sector to see how the same data engineering patterns apply to
              commercial project controls, materials, logistics and mining operations.
            </p>
          </div>

          <div className="industrial-tabs" role="tablist" aria-label="Industrial sectors">
            {sectorCapabilities.map((sector) => (
              <button
                type="button"
                key={sector.id}
                className={activeSector === sector.id ? "active" : ""}
                onClick={() => setActiveSector(sector.id)}
                role="tab"
                aria-selected={activeSector === sector.id}
              >
                {sector.tabLabel}
              </button>
            ))}
          </div>

          <div className="industrial-sector-panel">
            <div className="industrial-sector-copy">
              <span className="sector-check">✓</span>
              <h3>{currentSector.headline}</h3>
              <p>{currentSector.description}</p>
              <Link to="/contact" className="industrial-link">
                Discuss this sector <span aria-hidden="true">→</span>
              </Link>
            </div>
            <div className="industrial-sector-detail">
              <span className="eyebrow">Data signals</span>
              <h4>What we connect and reconcile</h4>
              <div>
                {currentSector.signals.map((signal) => (
                  <span key={signal}>{signal}</span>
                ))}
              </div>
              <p>
                The focus is not another dashboard. It is a governed operating data layer
                that connects physical activity, commercial controls and finance reporting.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="content-split">
          <div>
            <span className="eyebrow">Operating model</span>
            <h2>From field activity to commercial reporting without losing the source trail.</h2>
            <p>
              We model physical operating events, commercial controls and finance outcomes
              into datasets that can be reconciled, audited and used in Power BI.
            </p>
          </div>
          <div className="medallion-clean">
            <div>
              <strong>Capture</strong>
              <p>Bring in project systems, ERP data, weighbridge tickets, telematics, site registers and supplier files.</p>
            </div>
            <div>
              <strong>Reconcile</strong>
              <p>Match operating records against procurement, billing, cost codes, asset usage and finance ledgers.</p>
            </div>
            <div>
              <strong>Report</strong>
              <p>Publish governed Power BI layers for margin, yield, utilisation, sustainability and operational controls.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section alt">
        <div className="section-heading">
          <span className="eyebrow">Business value</span>
          <h2>What industrial operations teams get.</h2>
        </div>
        <div className="outcome-grid">
          {constructionOutcomes.map((outcome) => (
            <div className="outcome-card" key={outcome}>
              {outcome}
            </div>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <h2>Need clearer industrial operational reporting?</h2>
        <p>Start with project systems, ERP data, weighbridge records, telematics, site registers and reporting pain points.</p>
        <Link to="/contact" className="primary-btn">
          Book an industrial data review
        </Link>
      </section>

      <Footer />
    </div>
  );
}
